import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["passwordModal", "emailModal", "pendingEmail", "otpError", "verifyButton", "resendButton"]

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
  }

  openPasswordModal() {
    const modal = document.getElementById("password_modal")
    modal.classList.remove("hidden")
  }

  closePasswordModal() {
    const modal = document.getElementById("password_modal")
    modal.classList.add("hidden")
  }

  passwordSubmitEnd(event) {
    if (event.detail.success) {
      this.closePasswordModal()
    }
  }

  openEmailModal(email) {
    if (this.hasEmailModalTarget) {
      this.emailModalTarget.classList.remove("hidden")
      if (this.hasPendingEmailTarget) {
        this.pendingEmailTarget.textContent = email
      }
    }
  }

  closeEmailModal() {
    if (this.hasEmailModalTarget) {
      this.emailModalTarget.classList.add("hidden")
      this.clearOtpInputs()
      this.hideOtpError()
    }
  }

  async sendEmailVerification(email) {
    try {
      const response = await fetch("/profile/send_email_verification", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ email: email })
      })

      const data = await response.json()

      if (response.ok) {
        this.openEmailModal(email)
        // In development, open letter_opener in new tab to view the email
        if (document.body.dataset.railsEnv === "development") {
          window.open("/letter_opener", "_blank")
        }
        return { success: true }
      } else {
        return { success: false, error: data.error }
      }
    } catch (error) {
      return { success: false, error: "Failed to send verification code" }
    }
  }

  async verifyEmail() {
    const otpInputs = document.querySelectorAll('[data-otp-input-target="digit"]')
    const otp = Array.from(otpInputs).map(input => input.value).join("")

    if (otp.length !== 6) {
      this.showOtpError("Please enter the complete 6-digit code")
      return
    }

    this.setVerifyButtonLoading(true)

    try {
      const response = await fetch("/profile/verify_email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ otp: otp })
      })

      const data = await response.json()

      if (response.ok) {
        this.closeEmailModal()
        // Refresh the page to show updated email
        window.location.reload()
      } else {
        this.showOtpError(data.error)
        this.clearOtpInputs()
      }
    } catch (error) {
      this.showOtpError("Failed to verify code. Please try again.")
    } finally {
      this.setVerifyButtonLoading(false)
    }
  }

  async resendCode() {
    if (this.hasResendButtonTarget) {
      this.resendButtonTarget.disabled = true
      this.resendButtonTarget.textContent = "Sending..."
    }

    try {
      const response = await fetch("/profile/resend_verification", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        }
      })

      const data = await response.json()

      if (response.ok) {
        this.hideOtpError()
        this.clearOtpInputs()
        // In development, open letter_opener in new tab to view the email
        if (document.body.dataset.railsEnv === "development") {
          window.open("/letter_opener", "_blank")
        }
        // Show success briefly
        if (this.hasResendButtonTarget) {
          this.resendButtonTarget.textContent = "Sent!"
          setTimeout(() => {
            this.resendButtonTarget.textContent = "Resend"
            this.resendButtonTarget.disabled = false
          }, 2000)
        }
      } else {
        this.showOtpError(data.error)
        if (this.hasResendButtonTarget) {
          this.resendButtonTarget.textContent = "Resend"
          this.resendButtonTarget.disabled = false
        }
      }
    } catch (error) {
      this.showOtpError("Failed to resend code")
      if (this.hasResendButtonTarget) {
        this.resendButtonTarget.textContent = "Resend"
        this.resendButtonTarget.disabled = false
      }
    }
  }

  showOtpError(message) {
    if (this.hasOtpErrorTarget) {
      this.otpErrorTarget.textContent = message
      this.otpErrorTarget.classList.remove("hidden")
    }
  }

  hideOtpError() {
    if (this.hasOtpErrorTarget) {
      this.otpErrorTarget.classList.add("hidden")
    }
  }

  clearOtpInputs() {
    const otpInputs = document.querySelectorAll('[data-otp-input-target="digit"]')
    otpInputs.forEach((input, index) => {
      input.value = ""
      if (index === 0) input.focus()
    })
    this.updateVerifyButton()
  }

  setVerifyButtonLoading(loading) {
    if (this.hasVerifyButtonTarget) {
      this.verifyButtonTarget.disabled = loading
      this.verifyButtonTarget.textContent = loading ? "Verifying..." : "Verify"
    }
  }

  updateVerifyButton() {
    const otpInputs = document.querySelectorAll('[data-otp-input-target="digit"]')
    const otp = Array.from(otpInputs).map(input => input.value).join("")
    if (this.hasVerifyButtonTarget) {
      this.verifyButtonTarget.disabled = otp.length !== 6
    }
  }
}
